// Search Request Functions
import { request } from "../util/request"
import type {
  SearchResponse,
  SearchParams,
  SuggestResponse,
  SearchLocationsResponse,
} from "../types/search"

/**
 * Generic search method that handles all the nitty gritty details
 */
async function search(
  query: string,
  type: SearchParams["type"] = "all",
  params?: {
    page?: number
    per_page?: number
  },
): Promise<SearchResponse> {
  // Build URL according to Rails route: /api/v1/search/:type/(:q)
  const encodedQuery = encodeURIComponent(query)
  const queryParams = new URLSearchParams()

  if (params?.page) queryParams.set("page", params.page.toString())
  if (params?.per_page) queryParams.set("per_page", params.per_page.toString())

  const searchParams = queryParams.toString()
  const path = `/api/v1/search/${type}/${encodedQuery}${searchParams ? `?${searchParams}` : ""}`

  return request({
    queryKey: [
      "search",
      {
        path,
        options: { method: "GET" },
      },
    ],
  })
}

/**
 * Returns search result suggestions
 * @param query
 */
export async function searchSuggestions(
  query: string,
  type?: string,
): Promise<SuggestResponse> {
  const queryParams = new URLSearchParams({ q: query })
  if (type) queryParams.set("type", type)
  const path = `/api/v1/search/suggest?${queryParams.toString()}`

  return request({
    queryKey: [
      "search",
      {
        path,
        options: { method: "GET" },
      },
    ],
  })
}

/**
 * Search for posts only
 */
export async function searchPosts(
  query: string,
  params?: {
    page?: number
    per_page?: number
  },
): Promise<SearchResponse> {
  return search(query, "posts", params)
}
/**
 * Search for venues by name
 */
export async function searchVenues(
  query: string,
  params?: {
    page?: number
    per_page?: number
  },
): Promise<SearchResponse> {
  return search(query, "venues", params)
}

/**
 * Search for all types (bands, venues, concerts, etc.)
 */
export async function searchAll(
  query: string,
  params?: {
    page?: number
    per_page?: number
  },
): Promise<SearchResponse> {
  return search(query, "all", params)
}

/**
 * Search for bands only
 */
export async function searchBands(
  query: string,
  params?: {
    page?: number
    per_page?: number
  },
): Promise<SearchResponse> {
  return search(query, "bands", params)
}

/**
 * Combined bandNada-venue + Google Places search, for the event location field.
 */
export async function searchLocations(
  query: string,
): Promise<SearchLocationsResponse> {
  const path = `/api/v1/search/locations?q=${encodeURIComponent(query)}`

  return request({
    queryKey: [
      "search",
      {
        path,
        options: { method: "GET" },
      },
    ],
  })
}
